/**
 * Superadmin: which of this node's domains invite links point to (DNS tab,
 * Account/NodeDns.jsx).
 *
 * GET /api/node-settings/domain lists only the hosts deploy config says this
 * node is served on (WEB_APP_EXTRA_HOSTS plus the platform hostname), so the
 * choice can never be a domain that isn't live. Saving invalidates the cached
 * public settings (use_node_settings.js), which also carry custom_domain.
 *
 *   const { customDomain, defaultHost, options, loading, save } = useNodeDomain();
 */

import { useState, useEffect, useCallback } from 'react';
import { useApi } from './use_api';
import { useAuth } from '../context/AuthContext';
import { invalidateNodeSettings } from './use_node_settings';

export function useNodeDomain() {
    const api = useApi();
    const { user } = useAuth();
    const userId = user?.id;
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!userId) return undefined;
        let alive = true;
        api.get('/api/node-settings/domain', { params: { user_id: userId } })
            .then(d => { if (alive) setData(d); })
            .catch(() => {})
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [api, userId]);

    const save = useCallback(domain => api.put(
        '/api/node-settings/domain', { custom_domain: domain }, { params: { user_id: userId } },
    ).then(d => {
        invalidateNodeSettings();
        setData(d);
        return d;
    }), [api, userId]);

    return {
        // null while links use the platform hostname.
        customDomain: data?.custom_domain || null,
        defaultHost: data?.default_host || null,
        options: data?.options || [],
        loading,
        save,
    };
}

export default useNodeDomain;
